import React from 'react';
import { useTranslation } from 'react-i18next';
import { StepIndicator, FormNavigation, FormSection } from '../components/layout';
import { Checkbox, S3FileUpload } from '../components/ui';
import PessoaFisicaForm from './PessoaFisicaForm';
import PessoaJuridicaForm from './PessoaJuridicaForm';
import CreditQuestionnaireForm from './CreditQuestionnaireForm';
import { AuthorizationsForm, FormStep } from '../types';
import { OnboardingProvider, useOnboarding } from '../contexts/OnboardingContext';
import cargillLogo from '../assets/cargill-logo-191-61-c.png';
import { AUTHORIZATIONS } from '../hooks';
import { useDraftPersistence } from '../hooks/useDraftPersistence';

// ============================================
// Language Switcher
// ============================================

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <div className="flex items-center gap-1 text-sm">
      <button
        type="button"
        onClick={() => changeLanguage('pt-BR')}
        className={`px-2 py-1 rounded transition-colors ${
          currentLang === 'pt-BR' || currentLang === 'pt'
            ? 'bg-green-600 text-white font-medium'
            : 'text-gray-600 hover:bg-gray-200'
        }`}
        aria-label="Portugues"
      >
        PT
      </button>
      <button
        type="button"
        onClick={() => changeLanguage('en-US')}
        className={`px-2 py-1 rounded transition-colors ${
          currentLang === 'en-US' || currentLang === 'en'
            ? 'bg-green-600 text-white font-medium'
            : 'text-gray-600 hover:bg-gray-200'
        }`}
        aria-label="English"
      >
        EN
      </button>
    </div>
  );
};

// ============================================
// Componentes de estado (loading, sucesso, erro)
// ============================================

const LoadingState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <svg
          className="animate-spin h-12 w-12 text-green-600 mx-auto mb-4"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p className="text-gray-600">{t('onboarding.validatingAccess')}</p>
      </div>
    </div>
  );
};

const SuccessState: React.FC = () => {
  const { submission, customerInfo } = useOnboarding();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{t('onboarding.formSubmitted')}</h1>
        <p className="text-gray-600 mb-4">
          {t('onboarding.formReceivedMessage')}
        </p>
        {submission.submissionId && (
          <p className="text-sm text-gray-500 mb-4">
            {t('onboarding.protocol')}: <span className="font-mono font-medium">{submission.submissionId}</span>
          </p>
        )}
        {customerInfo && (
          <div className="text-sm text-gray-500 mb-6 p-4 bg-gray-50 rounded-lg">
            <p><strong>{t('onboarding.client')}:</strong> {customerInfo.name}</p>
            <p><strong>{t('onboarding.document')}:</strong> {customerInfo.cpf_cnpj}</p>
          </div>
        )}
        <p className="text-sm text-gray-500">
          {t('onboarding.notificationMessage')}
          <br />
          {t('onboarding.contactMessage')}
        </p>
      </div>
    </div>
  );
};

const ErrorState: React.FC = () => {
  const { error, errorType } = useOnboarding();
  const { t } = useTranslation();

  const getErrorIcon = () => {
    switch (errorType) {
      case 'TOKEN_EXPIRED':
        return (
          <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'TOKEN_CONSUMED':
        return (
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
    }
  };

  const getErrorBgColor = () => {
    switch (errorType) {
      case 'TOKEN_EXPIRED': return 'bg-orange-100';
      case 'TOKEN_CONSUMED': return 'bg-blue-100';
      default: return 'bg-red-100';
    }
  };

  const getErrorTitle = () => {
    switch (errorType) {
      case 'TOKEN_EXPIRED': return t('errors.linkExpired');
      case 'TOKEN_CONSUMED': return t('errors.linkUsed');
      default: return t('errors.accessDenied');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className={`w-16 h-16 ${getErrorBgColor()} rounded-full flex items-center justify-center mx-auto mb-4`}>
          {getErrorIcon()}
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{getErrorTitle()}</h1>
        <p className="text-gray-600 mb-6">{error}</p>
        <p className="text-sm text-gray-500">
          {t('errors.contactRepresentative')}
        </p>
      </div>
    </div>
  );
};

// ============================================
// Componente principal do formulário
// ============================================

const OnboardingForm: React.FC = () => {
  const {
    token,
    isLoading,
    error,
    customerInfo,
    customerType,
    requiredDocuments,
    steps,
    navigation,
    formData,
    formHandlers,
    fileUpload,
    submission,
  } = useOnboarding();

  const { t } = useTranslation();

  const {
    currentStep,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    completedSteps,
    stepErrors,
    goToStep,
    goToNextStep,
    goToPreviousStep,
  } = navigation;

  const { handlePFChange, handlePJChange, handleCreditChange, handleAuthorizationChange, handleAcceptAll } = formHandlers;
  const { uploadedFiles, handleFileUpload, handleFileRemoved } = fileUpload;
  const { isSubmitting, isSubmitted, handleSubmit } = submission;

  // Determinar se o usuário já começou a preencher (avançou da primeira etapa)
  const hasStartedFilling = currentStepIndex > 0;

  // Draft persistence (auto-save a cada 30s via localStorage)
  const { saveDraft, saveDraftToFile, loadDraftFromFile, isSaving, clearDraft } = useDraftPersistence({
    token,
    formData: formData as Record<string, unknown> & typeof formData,
    currentStep: currentStep as FormStep,
    uploadedFiles,
    onDraftLoaded: (data) => {
      formHandlers.setFormData(data.formData);
      navigation.goToStep(data.currentStep);
    },
  });

  // Limpar rascunho após submissão bem-sucedida
  React.useEffect(() => {
    if (isSubmitted) clearDraft();
  }, [isSubmitted, clearDraft]);

  // Estados de loading, sucesso e erro
  if (isLoading) {
    return <LoadingState />;
  }

  if (isSubmitted) {
    return <SuccessState />;
  }

  if (error && !isSubmitting) {
    return <ErrorState />;
  }

  // Renderizar conteúdo do step atual
  const renderStepContent = () => {
    switch (currentStep) {
      case 'identification':
        return customerType === 'PF' ? (
          <PessoaFisicaForm
            data={formData.pessoaFisica || {}}
            onChange={handlePFChange}
            errors={stepErrors}
            disabled={isSubmitting}
          />
        ) : (
          <PessoaJuridicaForm
            data={formData.pessoaJuridica || {}}
            onChange={handlePJChange}
            errors={stepErrors}
            disabled={isSubmitting}
          />
        );

      case 'credit-questionnaire':
        return (
          <CreditQuestionnaireForm
            data={formData.creditQuestionnaire || {}}
            onChange={handleCreditChange}
            errors={stepErrors}
            disabled={isSubmitting}
            prefilledName={customerInfo?.name || ''}
            prefilledCpf={customerInfo?.cpf_cnpj || ''}
            prefilledPhone={formData.pessoaFisica?.residentialAddress?.mobilePhone || ''}
            token={token || ''}
          />
        );

      case 'authorizations':
        return (
          <FormSection
            title={t('authorizations.title')}
            description={t('authorizations.description')}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            }
          >
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <Checkbox
                  label={t('authorizations.acceptAll')}
                  description={t('authorizations.acceptAllDescription')}
                  checked={formData.authorizations?.allAccepted || false}
                  onChange={handleAcceptAll}
                  disabled={isSubmitting}
                />
              </div>

              {AUTHORIZATIONS.map((auth) => (
                <div key={auth.key} className="border-b border-gray-200 pb-4 last:border-b-0">
                  <Checkbox
                    label={t(`authorizations.${auth.key}.title`)}
                    description={t(`authorizations.${auth.key}.description`)}
                    checked={
                      (formData.authorizations?.[auth.key as keyof AuthorizationsForm] as boolean) ||
                      false
                    }
                    onChange={handleAuthorizationChange(auth.key as keyof AuthorizationsForm)}
                    disabled={isSubmitting}
                  />
                </div>
              ))}
            </div>
          </FormSection>
        );

      case 'documents': {
        // Usar documentos da API ou fallback para lista padrao
        const documentsToShow = requiredDocuments.length > 0
          ? requiredDocuments.map((doc) => ({
              type: doc.id,
              label: doc.name,
              description: doc.description,
              required: doc.required,
            }))
          : customerType === 'PF'
            ? [
                { type: 'identity', label: t('documents.identity'), description: undefined, required: true },
                { type: 'cpf', label: t('documents.cpf'), description: undefined, required: true },
                { type: 'address_proof', label: t('documents.addressProof'), description: undefined, required: true },
                { type: 'income_proof', label: t('documents.incomeProof'), description: undefined, required: false },
              ]
            : [
                { type: 'cnpj', label: t('documents.cnpj'), description: undefined, required: true },
                { type: 'social_contract', label: t('documents.socialContract'), description: undefined, required: true },
                { type: 'balance_sheet', label: t('documents.balanceSheet'), description: undefined, required: true },
                { type: 'income_statement', label: t('documents.incomeStatement'), description: undefined, required: true },
              ];

        // Categorizar documentos: obrigatórios vs opcionais
        const requiredDocs = documentsToShow.filter((doc) => doc.required);
        const optionalDocs = documentsToShow.filter((doc) => !doc.required);

        const renderDocUpload = (doc: typeof documentsToShow[number]) => (
          <S3FileUpload
            key={doc.type}
            token={token || ''}
            documentType={doc.type}
            label={doc.label}
            description={doc.description}
            required={doc.required}
            disabled={isSubmitting}
            onUploadComplete={handleFileUpload(doc.type)}
            onFileRemoved={handleFileRemoved(doc.type)}
            onUploadError={(msg) => console.error(`Upload error ${doc.label}:`, msg)}
          />
        );

        return (
          <FormSection
            title={t('documents.title')}
            description={t('documents.description')}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          >
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{t('documents.instructions').split('.')[0]}.</strong> {t('documents.instructions').split('.').slice(1).join('.')}
                </p>
              </div>

              {/* Documentos obrigatórios */}
              {requiredDocs.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 text-xs font-bold">!</span>
                    {t('documents.requiredDocs')}
                    <span className="text-xs font-normal text-gray-500">({requiredDocs.length})</span>
                  </h3>
                  <div className="space-y-4 pl-1 border-l-2 border-red-200">
                    <div className="space-y-4 pl-4">
                      {requiredDocs.map(renderDocUpload)}
                    </div>
                  </div>
                </div>
              )}

              {/* Documentos opcionais */}
              {optionalDocs.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-xs">+</span>
                    {t('documents.optionalDocs')}
                    <span className="text-xs font-normal text-gray-500">({optionalDocs.length})</span>
                  </h3>
                  <div className="space-y-4 pl-1 border-l-2 border-gray-200">
                    <div className="space-y-4 pl-4">
                      {optionalDocs.map(renderDocUpload)}
                    </div>
                  </div>
                </div>
              )}

              {/* Resumo dos arquivos enviados */}
              {Object.keys(uploadedFiles).length > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium mb-2">
                    {t('documents.filesSent')}: {Object.keys(uploadedFiles).length} / {documentsToShow.length}
                  </p>
                  <ul className="text-xs text-green-700 space-y-1">
                    {Object.values(uploadedFiles).map((file) => (
                      <li key={file.storage_key} className="flex items-center gap-1">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {file.file_name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </FormSection>
        );
      }

      case 'review':
        return (
          <FormSection
            title={t('review.title')}
            description={t('review.description')}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            }
          >
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{t('review.summary')}:</strong>
                  <br />
                  {t('review.clientType')}: {customerType === 'PF' ? t('onboarding.personFisica') : t('onboarding.personJuridica')}
                  <br />
                  {t('review.authorizationsAccepted')}: {formData.authorizations?.allAccepted ? t('common.yes') : t('common.no')}
                </p>
              </div>

              <p className="text-gray-600">
                {t('review.confirmMessage')}
              </p>
            </div>
          </FormSection>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('onboarding.title')}</h1>
            <p className="text-gray-600 mt-1">
              {t('onboarding.subtitle', { type: customerType === 'PF' ? t('onboarding.personFisica') : t('onboarding.personJuridica') })}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <div className="hidden md:block">
              <img src={cargillLogo} alt="Cargill" className="h-10" />
            </div>
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <StepIndicator
          steps={steps}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={goToStep}
        />
      </div>

      {/* Form content */}
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 fade-in">
          {renderStepContent()}

          {Object.keys(stepErrors).length > 0 && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">
                {t('validation.stepHasErrors')}
              </p>
            </div>
          )}

          <FormNavigation
            onPrevious={goToPreviousStep}
            onNext={goToNextStep}
            onSave={saveDraft}
            onSaveToFile={saveDraftToFile}
            onLoadDraft={loadDraftFromFile}
            showLoadDraft={!hasStartedFilling}
            onSubmit={handleSubmit}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            showSubmit={isLastStep}
            isSubmitting={isSubmitting}
            isSaving={isSaving}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-5xl mx-auto px-4 mt-8 text-center">
        <p className="text-sm text-gray-500">
          {t('onboarding.contactMessage')}
          <br />
          {t('onboarding.linkValidityMessage')}
        </p>
      </div>
    </div>
  );
};

// ============================================
// Componente exportado com Provider
// ============================================

const OnboardingPage: React.FC = () => {
  return (
    <OnboardingProvider>
      <OnboardingForm />
    </OnboardingProvider>
  );
};

export default OnboardingPage;
